<template>
  <!-- ローディング -->
  <div style="height: 100vh;">
    <div v-if="isLoading" class="text-center mt-10 mb-10">
      <v-progress-circular indeterminate color="secondary" />
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import GraphqlAPI from '../../utils/GraphqlAPI'
import { Amplify, Auth, I18n } from 'aws-amplify'
import { CognitoUserSession, CognitoIdToken, CognitoAccessToken, CognitoRefreshToken } from 'amazon-cognito-identity-js'
import { awsExportsMobileAppDev, awsExportsMobileAppStg, awsExportsMobileAppProd } from '@/aws-exports'
import { translate } from '@/i18n'
import dayjs from 'dayjs'
import 'dayjs/locale/ja'
dayjs.locale('ja')

const getCampaignUserQuery = (campaignId) => `
  query getCampaignUser {
    getCampaignUser(campaign_id: "${campaignId}") {
      id
      course
      custom_objective_list {
        course_id
        custom_objective
      }
      is_mobile_app_used
    }
  }
`
const getCampaignUserCourse = async (campaignId) => {
  const { data: { getCampaignUser: data } } = await GraphqlAPI(getCampaignUserQuery(campaignId))
  return data
}

const checkAppUserStatusQuery = (campaignId) => `
  query checkAppUserStatus {
    checkAppUserStatus (campaign_id: "${campaignId}")
  }
`
const checkAppUserStatus = async (campaignId) => {
  const { data: { checkAppUserStatus: data } } = await GraphqlAPI(checkAppUserStatusQuery(campaignId))
  return data
}

const updateIsMobileAppUsedMutation = (campaignId, isMobileAppUsed) => `
  mutation updateIsMobileAppUsed {
    updateIsMobileAppUsed(
      campaign_id: "${campaignId}",
      is_mobile_app_used: ${isMobileAppUsed}
    ) {
      id
    }
  }
`
const updateIsMobileAppUsed = async (campaignId, isMobileAppUsed) => {
  const { data: { updateIsMobileAppUsed: data } } = await GraphqlAPI(updateIsMobileAppUsedMutation(campaignId, isMobileAppUsed))
  return data
}

const STEP_COUNT_STORAGE_KEY = 'STEP-COUNT-INFORMATION'
const WEIGHT_STORAGE_KEY = 'WEIGHT-INFORMATION'
const BLOOD_PRESSURE_STORAGE_KEY = 'BLOOD-PRESSURE-INFORMATION'

const readSessionStorageJson = (storageKey) => {
  const raw = sessionStorage.getItem(storageKey)
  if (raw == null || raw === '') {
    return null
  }
  try {
    return JSON.parse(raw)
  } catch (parseError) {
    console.error('sessionStorage JSON parse failed:', storageKey, parseError)
    return null
  }
}

export default Vue.extend({
  name: 'HCSignIn',
  props: ['signInConfig'],

  data () {
    return {
      error: '',
      logger: {},
      isLoading: false
    }
  },
  computed: {
    getCampaign () {
      return this.$store.getters.getCampaign
    },
    getRelationalCampaign () {
      return this.$store.getters.getRelationalCampaign
    },
    isProd () {
      return process.env.VUE_APP_DISP_STAGE_NAME === 'prod'
    }
  },

  async mounted () {
    this.isLoading = true
    await this.signIn()
    this.isLoading = false
  },
  methods: {
    async signIn () {
      // SessionStorageからアプリからの連携情報を取得
      let stepCountInformation = null
      let weightInformation = null
      let bloodPressureInformation = null
      try {
        await new Promise((resolve) => {
          const startTime = Date.now()
          const interval = setInterval(() => {
            stepCountInformation = readSessionStorageJson(STEP_COUNT_STORAGE_KEY)
            const timedOut = Date.now() - startTime >= 5000
            // 歩数キー（アプリ側で最後にデータ書込み）を取得できた場合、または5秒経過した場合に次へ進む
            if (timedOut || stepCountInformation) {
              clearInterval(interval)
              resolve()
            }
          }, 200) // 200ms間隔でチェック
        })
        weightInformation = readSessionStorageJson(WEIGHT_STORAGE_KEY)
        bloodPressureInformation = readSessionStorageJson(BLOOD_PRESSURE_STORAGE_KEY)

        // SessionStorage削除
        sessionStorage.removeItem(STEP_COUNT_STORAGE_KEY)
        sessionStorage.removeItem(WEIGHT_STORAGE_KEY)
        sessionStorage.removeItem(BLOOD_PRESSURE_STORAGE_KEY)

        // 連携情報が不正の場合、ログイン画面に遷移
        if (!stepCountInformation || !('auth_info' in stepCountInformation) || !('email_verified' in stepCountInformation.auth_info) ||
          !('last_auth_user' in stepCountInformation.auth_info) || !('id_token' in stepCountInformation.auth_info) ||
          !('access_token' in stepCountInformation.auth_info) || !('refresh_token' in stepCountInformation.auth_info)) {
          this.$router.push('/login')
          return
        }

        // Amplifyのaws_user_pools_web_client_idをアプリ用に設定しなおす
        if (process.env.VUE_APP_DISP_STAGE_NAME === 'prod') {
          Amplify.configure(awsExportsMobileAppProd)
        } else if (process.env.VUE_APP_DISP_STAGE_NAME === 'stg') {
          Amplify.configure(awsExportsMobileAppStg)
        } else {
          Amplify.configure(awsExportsMobileAppDev)
        }

        // amplifyに認証情報を設定
        const userSession = new CognitoUserSession({
          IdToken: new CognitoIdToken({ IdToken: stepCountInformation.auth_info.id_token }),
          AccessToken: new CognitoAccessToken({ AccessToken: stepCountInformation.auth_info.access_token }),
          RefreshToken: new CognitoRefreshToken({ RefreshToken: stepCountInformation.auth_info.refresh_token }),
          ClockDrift: 0
        })
        const cognitoUser = await Auth.createCognitoUser(stepCountInformation.auth_info.last_auth_user)
        await cognitoUser.setSignInUserSession(userSession)

        // 認証に失敗した場合、ログイン画面に遷移
        await Auth.currentSession()
      } catch (error) {
        console.error('Error setting user session:', error)
        this.$router.push('/login')
        return
      }

      // ここまでの認証成功/失敗はあくまでストレージのTokenの検証。AppSyncを実行して対象Cognitoプールの権限があるか確認できる
      try {
        this.$store.dispatch('setIsMobileApp', true)
        this.$store.dispatch('setIsAuthenticated', true)
        this.$store.dispatch('setEmailVerified', stepCountInformation.auth_info.email_verified)
        await this.$store.dispatch('initCampaign')
        await this.fetchUserCourseData()
        if (this.getCampaign.is_app_challenge) {
          await this.featchAppEntryStatus()
        }
        const appStatus = this.$store.getters.getAppEntryStatus

        // アプリ利用フラグ更新＋post_authenticate.pyの処理
        await updateIsMobileAppUsed(this.getCampaign.id, true)

        // メールアドレス認証未完了
        if (!stepCountInformation.auth_info.email_verified) {
          this.$router.push('/email')

        // コース選択済みorアプリチャレンジ申込み中
        } else if (this.$store.getters.getRegisteredCourse || appStatus === 'Temporary' || appStatus === 'Waiting' || appStatus === 'Completion') {
          const hasSteps = ('steps' in stepCountInformation) && stepCountInformation.steps && stepCountInformation.steps.length > 0
          const hasWeights = weightInformation && Array.isArray(weightInformation.weights) && weightInformation.weights.length > 0
          const hasBloodPressures = bloodPressureInformation && Array.isArray(bloodPressureInformation.blood_pressures) &&
            bloodPressureInformation.blood_pressures.length > 0

          if (hasSteps) {
            this.$store.dispatch('setWalkingPointListFromMobileApp', stepCountInformation.steps)
          }
          if (hasWeights) {
            this.$store.dispatch('setWeightListFromMobileApp', weightInformation.weights)
          }
          if (hasBloodPressures) {
            this.$store.dispatch('setBloodPressureListFromMobileApp', bloodPressureInformation.blood_pressures)
          }

          const now = dayjs()

          const isCampaignPeriod = (campaign) => {
            const start = dayjs(campaign.start, 'YYYY-MM-DD')
            // サイトクローズの翌日まで有効なので +1日
            const siteClose = dayjs(campaign.site_close, 'YYYY-MM-DD')
            return now >= start && now < siteClose.add(1, 'days')
          }

          const hasHealthData = hasSteps || hasWeights || hasBloodPressures

          // ヘルスデータ自動保存必要フラグを設定
          this.$store.dispatch('setNeedsSaveHealthDataFromMobileApp', isCampaignPeriod(this.getCampaign) && hasHealthData)
          this.$store.dispatch('setNeedsSaveHealthDataFromMobileAppRelational', isCampaignPeriod(this.getRelationalCampaign) && hasHealthData)

          this.$router.push({
            name: 'result',
            params: { referer: 'login' }
          })
        } else {
          this.$router.push('/course')
        }
      } catch (e) {
        this.$log.debug(e)
        try {
          await Auth.signOut()
        } catch (e2) {
          this.setError(e2)
        } finally {
          this.$store.dispatch('signOut')
          this.$router.push('/login')
        }
      }
    },

    setError (e) {
      const errorMessage = I18n.get(e.message || e)
      this.$log.debug(errorMessage)
      if (errorMessage === 'Incorrect username or password.') {
        this.error = `${translate('auth.errors.incorrectIdOrPass')}`
      } else if (errorMessage === 'User does not exist.') {
        this.error = `${translate('auth.errors.userNotRegistered')}`
      } else if (errorMessage === 'Password attempts exceeded') {
        this.error = `${translate('auth.errors.limitExceededException')}`
      } else if (errorMessage === 'PostAuthentication failed with error .') {
        this.error = `${translate('auth.errors.userLambdaValidation')}`
      } else {
        this.error = `${translate('auth.errors.unexpectedError')}`
      }
    },
    async fetchUserCourseData () {
      const getCampaignUserResult = await getCampaignUserCourse(this.getCampaign.id)
      const customObjectiveList = getCampaignUserResult.custom_objective_list
      if (customObjectiveList.length > 0) {
        this.$store.dispatch('setRegisteredCourse', true)
      } else {
        this.$store.dispatch('setRegisteredCourse', false)
      }
    },
    async featchAppEntryStatus () {
      const appEntryStatus = await checkAppUserStatus(this.getCampaign.id)
      this.$store.dispatch('setAppEntryStatus', appEntryStatus)
    }
  }
})
</script>
